import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading, Reveal } from "@/components/primitives";
import { api, trackEvent } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, CalendarDays, Play, Newspaper, Radio, Crown, ArrowUpRight, Check } from "lucide-react";

const PERKS = [
  { icon: CalendarDays, t: "Live Seminars", d: "A schedule of live, interactive sessions with Q-Core research." },
  { icon: Play, t: "Recording Library", d: "Full back-catalogue of past trainings, on demand." },
  { icon: Newspaper, t: "Strategy Write-ups", d: "In-depth educational material on strategy and risk." },
  { icon: Radio, t: "Signals Included", d: "VIP bundles full Quantum Signals access at no extra cost." },
];

export default function VIPProduct() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  useEffect(() => { trackEvent("page_view", { page: "vip_product" }); }, []);
  const buy = async () => {
    if (!user) { nav("/register"); return; }
    setBusy(true);
    try { const { data } = await api.post("/payments/checkout", { lookup_key: "vip_membership", origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch { setBusy(false); }
  };
  return (
    <div>
      <PageHeader eyebrow="VIP Membership"
        title="Go deeper. Learn live. Get everything."
        sub="VIP unlocks live seminars, the full training-recording library, in-depth strategy write-ups — and bundles Quantum Signals access. Education and intelligence for serious members.">
        <button onClick={buy} disabled={busy} data-testid="vip-buy" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#0E9E90] text-white font-medium shadow-[0_0_24px_rgba(14,158,144,0.35)] hover:bg-[#1E4BCC] disabled:opacity-60 transition-colors">
          {busy ? "Starting…" : "Become VIP — $499/mo"} <ArrowUpRight size={17} />
        </button>
      </PageHeader>

      <Section grid>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PERKS.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.05}>
              <div className="qs-beam h-full rounded-xl border border-white/5 bg-[#0A0B10] p-6">
                <span className="grid place-items-center h-11 w-11 rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#F59E0B] mb-4"><f.icon size={20} strokeWidth={1.5} /></span>
                <h3 className="font-bold text-white">{f.t}</h3>
                <p className="text-sm text-[#94A3B8] mt-1.5 leading-relaxed">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section grid>
        <div className="rounded-2xl border border-[#F59E0B]/25 bg-gradient-to-b from-[#1A1608] to-[#0A0B10] p-8 md:p-12 qs-aurora">
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <SectionHeading eyebrow="Membership includes" title="The complete VIP experience." />
              <ul className="mt-6 space-y-2.5">
                {["Live seminar schedule with reminders", "On-demand training recording library", "Strategy & education write-ups", "Full Quantum Signals feed included", "Priority recognition across the platform"].map((x) => (
                  <li key={x} className="flex items-start gap-2.5 text-sm text-[#cbd5e1]"><Check size={16} className="text-[#00E676] mt-0.5 shrink-0" /> {x}</li>
                ))}
              </ul>
            </div>
            <div className="text-center rounded-2xl border border-white/10 bg-black/30 p-8">
              <Crown size={30} className="text-[#F59E0B] mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-mono text-4xl text-white">$499<span className="text-base text-[#64748B]">/month</span></p>
              <button onClick={buy} disabled={busy} data-testid="vip-buy-2" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#F59E0B] text-[#0A0B10] font-semibold hover:brightness-110 disabled:opacity-60 transition-all">
                {busy ? "Starting…" : (user ? "Become VIP" : "Create account & join")} <ArrowUpRight size={16} />
              </button>
              <p className="mt-4 text-[11px] text-[#64748B] font-mono">Educational membership. Not financial advice. Markets carry risk; results are not guaranteed.</p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
